/**
 * Email verification (medopd-docs 96): prove control of an address with an emailed 6-digit code. Mirrors
 * medopd-backend's emailverification package.
 *
 * - Sign-up (doctor / lab listing): requestCode → verifyCode → send `emailGrant` with the sign-up.
 * - A sign-in paused because a self-registered account never proved its email: the login answer carries
 *   { emailVerificationRequired, pendingToken, challengeId, emailHint }; finish with completeLogin (password
 *   sign-in) or doctorListing.signIn.completeEmailVerification (SMS sign-in). resendLoginCode sends a new code.
 */
import { fetchApi } from '../client';
import { captureStaffSession } from '../sessionToken';
import type { EmailLoginChallenge } from '../../../types';

export type EmailCodePurpose = 'DOCTOR_SIGNUP' | 'LAB_SIGNUP';

export interface EmailCodeSent {
  challengeId: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface EmailVerified {
  emailGrant: string;
  expiresInSeconds: number;
}

const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body), skipBodyLog: true, skipAuth: true });

/** The pause body from a login / SMS sign-in response (`data` already unwrapped by fetchApi), if it is one. */
export function emailChallengeOf(data: unknown): EmailLoginChallenge | null {
  if (!data || typeof data !== 'object') return null;
  const outer = data as { emailVerificationRequired?: boolean; data?: unknown };
  const d = (outer.emailVerificationRequired ? outer : outer.data) as Partial<EmailLoginChallenge> & { emailVerificationRequired?: boolean } | undefined;
  if (!d || !d.emailVerificationRequired || !d.pendingToken || !d.challengeId) return null;
  return {
    pendingToken: d.pendingToken,
    challengeId: d.challengeId,
    emailHint: d.emailHint ?? '',
    expiresInSeconds: d.expiresInSeconds ?? 600,
    resendAfterSeconds: d.resendAfterSeconds ?? 30,
  };
}

export const emailVerificationModule = {
  /** Emails a code. The answer is the same whether or not an account uses the address. */
  requestCode: (email: string, purpose: EmailCodePurpose) =>
    fetchApi<EmailCodeSent>('/api/public/email-verification/request', post({ email, purpose })),
  /** The right code buys a 30-minute, single-use grant for this email and purpose. */
  verifyCode: (challengeId: string, email: string, code: string) =>
    fetchApi<EmailVerified>('/api/public/email-verification/verify', post({ challengeId, email, code })),
  /** A new code for a paused sign-in (the same pending token keeps working; the old code stops). */
  resendLoginCode: (pendingToken: string, challengeId: string) =>
    fetchApi<EmailLoginChallenge & { emailVerificationRequired: true }>('/api/public/email-verification/login/resend',
      post({ pendingToken, challengeId })),
  /** Finishes a password sign-in paused for the email: answers exactly like a login (a session, or mfaRequired). */
  completeLogin: async (pendingToken: string, challengeId: string, code: string) => {
    const response = await fetchApi<unknown>('/api/auth/email-verification/complete', post({ pendingToken, challengeId, code }));
    captureStaffSession(response);
    return response;
  },
};
